class GameAuth {
    constructor() {
        this.apiUrl = '/api';
        this.authToken = null;
        this.user = null;
        this.initialized = false;
    }

    // Get stored auth token
    getStoredAuth() {
        return localStorage.getItem('gameAuth');
    }

    // Store auth token
    setAuth(token, user) {
        this.authToken = token;
        this.user = user;
        localStorage.setItem('gameAuth', token);

        if (user) {
            localStorage.setItem('gameUser', JSON.stringify(user));
        }
    }

    // Clear auth
    clearAuth() {
        this.authToken = null;
        this.user = null;
        localStorage.removeItem('gameAuth');
        localStorage.removeItem('gameUser');
    }

    // API call helper
    async apiCall(endpoint, data = null, method = 'GET') {
        try {
            const options = {
                method,
                headers: {
                    'Content-Type': 'application/json',
                },
            };

            if (data) {
                options.body = JSON.stringify(data);
            }

            const response = await fetch(`${this.apiUrl}${endpoint}`, options);
            return await response.json();
        } catch (error) {
            console.error('API call failed:', error);
            return { success: false, error: 'Connection failed' };
        }
    }

    // Validate access code
    async validateAccessCode(code) {
        const result = await this.apiCall(
            '/validate-access-code',
            {
                code: code.trim().toUpperCase(),
            },
            'POST'
        );

        return result;
    }

    // Main authentication check
    async checkAuth() {
        if (import.meta.env.DEV) {
            this.setAuth('dev-token', { username: 'Dev' });
            console.log('✅ Dev mode — skipping access code');
            return true;
        }

        // Check for existing auth token in localStorage
        const storedAuth = this.getStoredAuth();
        if (storedAuth) {
            // Check if user data exists
            const storedUser = localStorage.getItem('gameUser');
            if (storedUser) {
                try {
                    const user = JSON.parse(storedUser);
                    this.authToken = storedAuth;
                    this.user = user;
                    console.log(`✅ Welcome back, ${user.username || 'Player'}!`);
                    return true;
                } catch {
                    console.log('❌ Invalid stored user data');
                    this.clearAuth();
                }
            } else {
                // Token exists but no user data, clear it
                this.clearAuth();
            }
        }

        // No existing auth found, need access code
        console.log('🔑 Authentication required');
        return false;
    }

    // Show auth modal
    showAuthModal() {
        return new Promise((resolve) => {
            // Check for auth code in query parameters
            const urlParams = new URLSearchParams(window.location.search);
            const codeFromUrl =
                urlParams.get('code') || urlParams.get('auth') || urlParams.get('access_code');

            // Create modal HTML
            const modal = document.createElement('div');
            modal.id = 'authModal';
            modal.innerHTML = `
                <div class="auth-overlay">
                  <div class="auth-modal">
                    <div class="auth-header">
                      <h2>Game Access Required</h2>
                      <p>Enter your Discord access code to play</p>
                    </div>
                    <div class="auth-form">
                      <input
                        type="text"
                        id="accessCodeInput"
                        placeholder="POKEMON-XXXX"
                        autocomplete="off"
                        autocorrect="off"
                        autocapitalize="characters"
                        spellcheck="false"
                        inputmode="text"
                        style="text-transform: uppercase"
                        value="${codeFromUrl ? codeFromUrl.toUpperCase() : ''}"
                      />
                      <div class="auth-buttons">
                        <button id="submitCode" type="button">Enter Game</button>
                      </div>
                      <div id="authError" class="auth-error" style="display: none;"></div>
                      <div class="auth-help">
                        <p>Don't have a code? Join our <a href='https://discord.gg/fpVMxprNMP' target='_blank'>Discord server</a> and request access!</p>
                      </div>
                    </div>
                  </div>
                </div>
            `;

            // Simplified universal styles - no device detection
            const style = document.createElement('style');
            style.textContent = `
                .auth-overlay {
                  position: fixed;
                  top: 0;
                  left: 0;
                  width: 100vw;
                  height: 100vh;
                  background: rgba(0, 0, 0, 0.95);
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  z-index: 10000;
                  font-family: 'BattleFont', sans-serif;
                  padding: 12px;
                  box-sizing: border-box;
                }

                .auth-modal {
                  background: rgb(35, 44, 53);
                  border-radius: 20px;
                  border: 4px solid rgb(25, 25, 25);
                  color: white;
                  text-align: center;
                  width: calc(100vw - 60px);
                  height: calc(100vh - 60px);
                  padding: 60px 40px;
                  box-sizing: border-box;
                  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.6);
                  display: flex;
                  flex-direction: column;
                  justify-content: center;
                }

                .auth-header h2 {
                  margin: 0 0 20px 0;
                  color: white;
                  font-size: 2.5em;
                  text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.7);
                }

                .auth-header p {
                  margin: 0 0 40px 0;
                  color: #ccc;
                  font-size: 1.4em;
                  line-height: 1.4;
                }

                .auth-form input {
                  width: 100%;
                  padding: 35px 30px;
                  font-size: 32px;
                  border: 4px solid rgb(25, 25, 25);
                  border-radius: 16px;
                  background: rgb(45, 45, 45);
                  color: white;
                  font-family: 'BattleFont', monospace;
                  text-align: center;
                  margin-bottom: 45px;
                  box-sizing: border-box;
                  letter-spacing: 4px;
                  font-weight: bold;

                  /* Universal mobile input handling */
                  -webkit-appearance: none;
                  -moz-appearance: none;
                  appearance: none;
                  touch-action: manipulation;
                  -webkit-tap-highlight-color: transparent;
                  -webkit-touch-callout: none;
                  -webkit-user-select: text;
                  -moz-user-select: text;
                  -ms-user-select: text;
                  user-select: text;
                }

                .auth-form input:focus {
                  outline: none;
                  border-color: #00AE86;
                  background: rgb(50, 50, 50);
                  box-shadow: 0 0 20px rgba(0, 174, 134, 0.5);
                }

                .auth-buttons button {
                  background: rgb(25, 25, 25);
                  color: white;
                  border: none;
                  padding: 35px 60px;
                  font-size: 28px;
                  border-radius: 16px;
                  cursor: pointer;
                  font-family: 'BattleFont', sans-serif;
                  transition: all 0.2s;
                  min-width: 280px;
                  font-weight: bold;
                  text-transform: uppercase;
                  letter-spacing: 1px;

                  /* Ensure button works on mobile */
                  touch-action: manipulation;
                  -webkit-tap-highlight-color: rgba(0, 174, 134, 0.3);
                  user-select: none;
                }

                .auth-buttons button:hover {
                  background: rgb(45, 45, 45);
                  transform: translateY(-3px);
                  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.5);
                }

                .auth-buttons button:active {
                  background: rgb(0, 174, 134);
                  transform: translateY(-1px);
                  box-shadow: 0 4px 12px rgba(0, 174, 134, 0.4);
                }

                .auth-buttons button:disabled {
                  background: rgb(60, 60, 60);
                  cursor: not-allowed;
                  transform: none;
                  box-shadow: none;
                }

                .auth-error {
                  color: #ff6b6b;
                  margin-top: 25px;
                  padding: 25px;
                  background: rgba(255, 107, 107, 0.15);
                  border-radius: 12px;
                  font-size: 18px;
                  border: 3px solid rgba(255, 107, 107, 0.4);
                  font-weight: bold;
                }

                .auth-help {
                  margin-top: 40px;
                  font-size: 18px;
                  color: #aaa;
                  line-height: 1.6;
                }

                .auth-help a {
                  color: #00AE86;
                  text-decoration: none;
                  font-weight: bold;
                }

                .auth-help a:hover {
                  text-decoration: underline;
                  color: #00c99a;
                }

                /* Prevent iOS zoom - but keep large font size */
                .auth-form input {
                  font-size: max(32px, 16px);
                }
            `;

            document.head.appendChild(style);
            document.body.appendChild(modal);

            const input = document.getElementById('accessCodeInput');
            const button = document.getElementById('submitCode');
            const errorDiv = document.getElementById('authError');

            // Submit function - extract to avoid conflicts
            const performSubmit = async () => {
                const code = input.value.trim().toUpperCase();

                if (!code) {
                    this.showError(errorDiv, 'Please enter an access code');
                    return;
                }

                button.disabled = true;
                button.textContent = 'Validating...';
                errorDiv.style.display = 'none';

                try {
                    const result = await this.validateAccessCode(code);

                    if (result.success) {
                        this.setAuth(result.authToken, result.user);
                        document.body.removeChild(modal);
                        document.head.removeChild(style);

                        // Clean up URL if code was from query params
                        if (codeFromUrl) {
                            const url = new URL(window.location);
                            url.searchParams.delete('code');
                            url.searchParams.delete('auth');
                            url.searchParams.delete('access_code');
                            window.history.replaceState({}, document.title, url);
                        }

                        resolve(result.user);
                    } else {
                        this.showError(errorDiv, result.error || 'Invalid access code');
                        button.disabled = false;
                        button.textContent = 'Enter Game';
                    }
                } catch {
                    this.showError(errorDiv, 'Connection error. Please try again.');
                    button.disabled = false;
                    button.textContent = 'Enter Game';
                }
            };

            // Format input as user types - allow lowercase but display as uppercase
            input.addEventListener('input', (e) => {
                let value = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();

                // Find the first digit and insert dash before it
                const dashIndex = value.search(/\d/);
                if (dashIndex > 0) {
                    value = `${value.slice(0, dashIndex)}-${value.slice(dashIndex)}`;
                }

                e.target.value = value;
            });

            // If code was pre-filled from URL, format it properly
            if (codeFromUrl) {
                const event = new Event('input', { bubbles: true });
                input.dispatchEvent(event);
                console.log(`🔗 Auto-filled access code from URL: ${codeFromUrl}`);
            }

            // Mobile keyboard handling - improved
            input.addEventListener('click', (e) => {
                e.stopPropagation();
                input.focus();
            });

            input.addEventListener('touchstart', (e) => {
                e.stopPropagation();
                input.focus();
            });

            input.addEventListener('touchend', (e) => {
                e.stopPropagation();
                input.focus();
                // Small delay to ensure focus works on iOS
                setTimeout(() => {
                    input.setSelectionRange(input.value.length, input.value.length);
                }, 50);
            });

            // Modal click to focus - but avoid conflicts with input and button
            modal.addEventListener('click', (e) => {
                // Don't interfere with input, button, or links
                if (e.target === input || e.target === button || e.target.tagName === 'A') {
                    return;
                }

                // If clicking on modal background or other areas, focus input
                if (e.target === modal || e.target.closest('.auth-modal')) {
                    input.focus();
                }
            });

            // Submit on Enter
            input.addEventListener('keypress', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    performSubmit();
                }
            });

            // Button click handlers - single reliable handler
            button.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                performSubmit();
            });

            // Additional touchend for mobile - but prevent double submission
            let submitInProgress = false;
            button.addEventListener('touchend', (e) => {
                e.preventDefault();
                e.stopPropagation();

                if (!submitInProgress) {
                    submitInProgress = true;
                    performSubmit().finally(() => {
                        setTimeout(() => {
                            submitInProgress = false;
                        }, 1000);
                    });
                }
            });

            // Auto-focus for desktop, manual focus for mobile
            setTimeout(() => {
                input.focus();
                if (codeFromUrl) {
                    input.setSelectionRange(input.value.length, input.value.length);
                    console.log('💡 Code pre-filled! Press Enter or tap "Enter Game" to submit.');
                }
            }, 200);
        });
    }

    showError(errorDiv, message) {
        errorDiv.textContent = message;
        errorDiv.style.display = 'block';
    }

    // Main initialization function
    async initialize() {
        if (this.initialized) return true;

        console.log('Initializing game authentication...');

        // const isAuthenticated = await this.checkAuth();
        // const isProd = !import.meta.env.DEV;
        // if (!isAuthenticated && isProd) {
        //     console.log('🔑 Showing authentication modal...');
        //     await this.showAuthModal();
        // }

        this.initialized = true;
        console.log('Authentication complete!');
        return true;
    }

    // Get current user info
    getCurrentUser() {
        return this.user;
    }

    // Check if user is authenticated
    isAuthenticated() {
        return !!this.authToken;
    }
}

// Global auth instance
window.gameAuth = new GameAuth();
